"use client";

import { useEffect } from "react";
import { CookieConsentCategory, CookieConsentStatus } from "@lib/analytics/cookieConsent";
import { useCookieConsent } from "@lib/analytics/useCookieConsent";
import { useAuth } from "@lib/auth/useAuth";
import { getDatadogRum } from "@lib/monitoring/datadogHelper";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import type { DatadogFlagsClient } from "./initializeDatadogFlags";

interface ExperimentSubjectSyncProps {
    enabled: boolean;
    monitoringReady: boolean;
    onReady?: (client: DatadogFlagsClient) => void;
}

/** Initializes the flag SDK after consent, session and RUM readiness; renders no UI. */
export const ExperimentSubjectSync = ({
    enabled,
    monitoringReady,
    onReady,
}: ExperimentSubjectSyncProps): null => {
    const { session, isLoading } = useAuth();
    const consent = useCookieConsent(CookieConsentCategory.STATISTICS);
    const targetingKey = session?.ucid || session?.anonymousId;
    const isLoggedIn = Boolean(session?.ucid);

    useEffect(() => {
        if (
            !enabled ||
            !monitoringReady ||
            isLoading ||
            !targetingKey ||
            consent !== CookieConsentStatus.GRANTED
        )
            return;

        let cancelled = false;
        let stop: (() => void) | undefined;

        import("./initializeDatadogFlags")
            .then(({ startDatadogFlags }) => {
                if (cancelled) return;
                const rum = getDatadogRum();
                if (!rum?.getInitConfiguration()) return;
                // Preserve other RUM properties; align returning sessions as well as logins.
                rum.setUserProperty("id", targetingKey);
                stop = startDatadogFlags({ targetingKey, isLoggedIn, onReady });
            })
            .catch(() => {
                if (!cancelled)
                    Logger.error(ServiceType.WEB, "Failed to load Datadog feature flags", {
                        tag: LogTag.FEATURE_FLAGS,
                    });
            });

        return () => {
            cancelled = true;
            stop?.();
        };
    }, [enabled, monitoringReady, isLoading, targetingKey, isLoggedIn, consent, onReady]);

    return null;
};
